import type { IStringSchema } from '@ksyos/koi';
import { Koi } from '@ksyos/koi';
import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { IGeneralPractitioner } from '@common/interfaces/generalPractitioner';
import type { Override } from '@common/utils/utilityTypes';
import type { TypedGeneralPractitionerSchema } from '@common/validation/customControls/generalPractitioner';
import type { IPatientAddEditValidated } from '@common/validation/patient';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { generalPractitionerSchema } from '@common/validation/customControls/generalPractitioner';
import { getProcessActivityAssetsSchema } from '@common/validation/processActivityAsset';
import { emailSchema } from '@common/validation/schemas';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import { getTextInputStringSchema } from '@common/validation/utils/getTextInputStringSchema';

type Patient = Pick<IPatientAddEditValidated, 'emailAddress' | 'homePhoneNumber' | 'mobileNumber'>;

type TypedPatientSchema = TypedSchema<Record<keyof Patient, IStringSchema<TypedPatientSchema>>>;

type TypedBariPreReferralSchema = Override<
    TypedSchema<BariPreReferralData>,
    {
        generalPractitioner: TypedGeneralPractitionerSchema;
        patient: TypedPatientSchema;
    }
>;
export type BariPreReferralData = {
    assets: ProcessActivityAsset[];
    generalPractitioner: IGeneralPractitioner;
    medisch_dossier_nummer: string | null;
    patient: Patient;
    toelichting: string | null;
    toestemming_van_patient: boolean;
};

export type BariPreReferralReadData = BariPreReferralData & {
    patient_emailadres: string;
    patient_telefoonnummer: string;
    patient_telefoonnummer_mobiel: string;
};

export type BariPreReferralReadKey = keyof BariPreReferralReadData;
export type BariPreReferralReadLabel =
    | 'E-mailadres patiënt'
    | 'Huisarts van de patiënt'
    | 'Mobiel telefoonnummer patiënt'
    | 'Telefoonnummer patiënt'
    | 'Toelichting'
    | 'Verwijsbrief'
    | 'MDN (Medisch Dossier Nummer)';
export type BariPreReferralSectionTitle =
    | 'Contactgegevens patiënt'
    | 'Huisarts van de patiënt'
    | 'Medisch dossier nummer'
    | 'Verwijsbrief';

export const bariPreReferralReadSchema: TypedReadSchema<
    BariPreReferralReadKey,
    BariPreReferralReadLabel,
    BariPreReferralSectionTitle
>[] = [
    {
        items: [
            { key: 'assets', label: 'Verwijsbrief' },
            { key: 'toelichting', label: 'Toelichting' },
        ],
        sectionTitle: 'Verwijsbrief',
    },
    {
        items: [{ key: 'medisch_dossier_nummer', label: 'MDN (Medisch Dossier Nummer)' }],
        sectionTitle: 'Medisch dossier nummer',
    },
    {
        items: [
            { key: 'patient_telefoonnummer', label: 'Telefoonnummer patiënt' },
            { key: 'patient_telefoonnummer_mobiel', label: 'Mobiel telefoonnummer patiënt' },
            { key: 'patient_emailadres', label: 'E-mailadres patiënt' },
        ],
        sectionTitle: 'Contactgegevens patiënt',
    },
    {
        items: [{ key: 'generalPractitioner', label: 'Huisarts van de patiënt' }],
        sectionTitle: 'Huisarts van de patiënt',
    },
];

const bariPreReferralFinal: TypedBariPreReferralSchema = {
    assets: getProcessActivityAssetsSchema({ min: 1 }),
    generalPractitioner: generalPractitionerSchema(true),
    medisch_dossier_nummer: Koi.string().numeric().min(9).max(9),
    patient: {
        emailAddress: emailSchema,
        homePhoneNumber: getTextInputStringSchema(true, 255),
        mobileNumber: getTextInputStringSchema(false, 255),
    },
    toelichting: getTextAreaStringSchema(true),
    toestemming_van_patient: Koi.boolean().valid(true),
};

const bariPreReferralDraft: TypedBariPreReferralSchema = {
    assets: getProcessActivityAssetsSchema(),
    generalPractitioner: generalPractitionerSchema(true),
    medisch_dossier_nummer: getTextAreaStringSchema(true),
    patient: {
        emailAddress: getTextInputStringSchema(true, 255),
        homePhoneNumber: getTextInputStringSchema(true, 255),
        mobileNumber: getTextInputStringSchema(true, 255),
    },
    toelichting: getTextAreaStringSchema(true),
    toestemming_van_patient: Koi.boolean(),
};

export const bariPreReferralDraftSchema = Koi.object(bariPreReferralDraft);
export const bariPreReferralFinalSchema = Koi.object(bariPreReferralFinal);
